import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IMedication } from '../../../../../pages/common';
import type { IntakeQuestionnaireState } from '../../types';
import type {
  DeleteIntakeMedicationPayload,
  IntakeMedicationThunks,
  SetIntakeHasNoMedicationsPayload,
} from './types';

export function addIntakeMedicationCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  addCreateIntakeMedicationCases(builder, thunks);
  addUpdateIntakeMedicationCases(builder, thunks);
  addDeleteIntakeMedicationCases(builder, thunks);
  addDeleteAllIntakeMedicationsCases(builder, thunks);
  addSetIntakeHasNoMedicationsCases(builder, thunks);
}

function addCreateIntakeMedicationCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  const { createIntakeMedication } = thunks;

  builder
    .addCase(createIntakeMedication.pending, (state) => {
      state.createMedicationLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeMedication.fulfilled,
      (state, action: PayloadAction<IMedication>) => {
        state.createMedicationLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        if (!state.intakeQuestionnaireDetails.medications) {
          state.intakeQuestionnaireDetails.medications = [];
        }
        state.intakeQuestionnaireDetails.medications.push(action.payload);
      },
    )
    .addCase(createIntakeMedication.rejected, (state, action) => {
      state.createMedicationLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateIntakeMedicationCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  const { updateIntakeMedication } = thunks;

  builder
    .addCase(updateIntakeMedication.pending, (state) => {
      state.updateMedicationLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeMedication.fulfilled,
      (state, action: PayloadAction<IMedication>) => {
        state.updateMedicationLoading = false;
        const list = state.intakeQuestionnaireDetails?.medications;
        if (!list) return;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateIntakeMedication.rejected, (state, action) => {
      state.updateMedicationLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeMedicationCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  const { deleteIntakeMedication } = thunks;

  builder
    .addCase(deleteIntakeMedication.pending, (state) => {
      state.deleteMedicationLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeMedication.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeMedicationPayload }>) => {
        state.deleteMedicationLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        state.intakeQuestionnaireDetails.medications = (
          state.intakeQuestionnaireDetails.medications ?? []
        ).filter((row) => row.id !== action.meta.arg.medicationId);
      },
    )
    .addCase(deleteIntakeMedication.rejected, (state, action) => {
      state.deleteMedicationLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteAllIntakeMedicationsCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  const { deleteAllIntakeMedications } = thunks;

  builder
    .addCase(deleteAllIntakeMedications.pending, (state) => {
      state.deleteAllMedicationsLoading = true;
      state.error = null;
    })
    .addCase(deleteAllIntakeMedications.fulfilled, (state) => {
      state.deleteAllMedicationsLoading = false;
      if (!state.intakeQuestionnaireDetails) return;
      state.intakeQuestionnaireDetails.medications = [];
      state.intakeQuestionnaireDetails.has_no_medications = true;
    })
    .addCase(deleteAllIntakeMedications.rejected, (state, action) => {
      state.deleteAllMedicationsLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addSetIntakeHasNoMedicationsCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicationThunks,
) {
  const { setIntakeHasNoMedications } = thunks;

  builder
    .addCase(setIntakeHasNoMedications.pending, (state) => {
      state.setHasNoMedicationsLoading = true;
      state.error = null;
    })
    .addCase(
      setIntakeHasNoMedications.fulfilled,
      (state, action: PayloadAction<void, string, { arg: SetIntakeHasNoMedicationsPayload }>) => {
        state.setHasNoMedicationsLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        state.intakeQuestionnaireDetails.has_no_medications = action.meta.arg.hasNoMedications;
      },
    )
    .addCase(setIntakeHasNoMedications.rejected, (state, action) => {
      state.setHasNoMedicationsLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
